import React from "react";
import { useQuery } from "react-query";
import { trackMatchInteraction } from "../../../../libraries/_modules/tracking/helpers";
import { composeVideoList, removeDuplicates } from "../../../mls-match-feed/utils";
import { fetchClient } from "../../../shared/api/fetching";
import {
  apiURL,
  forgeMatchContentVideosAPI,
  forgeMatchVideosAPI,
} from "../../../shared/api/variables";
import { useReactAppState } from "../../../shared/containers/utils";
import { statusTypes, useMatchData } from "../../utils";
import { MatchWatchLive } from "./MatchWatchLive";
import { MatchWatchPost } from "./MatchWatchPost";
import { MatchWatchPre } from "./MatchWatchPre";
import { StyledMatchWatch } from "./StyledMatchWatch";
import MatchHotelBook from "./MatchHotelBook";

export const MatchWatch = ({ position = null, showChevron }) => {
  const { videoTags } = useReactAppState();
  const { slug, status, promoInfo, queryConfig, matchSportecId } = useMatchData();

  const { firstPartyTickets, thirdPartyTickets } = promoInfo || {};

  const forgeMatchVideosEndpoint = forgeMatchVideosAPI({ matchSportecId });
  const forgeMatchContentVideosEndpoint = forgeMatchContentVideosAPI({
    matchSlug: slug,
  });

  const { data: forgeMatchVideos } = useQuery({
    queryKey: [`forge-match-videos`, { matchSportecId }],
    queryFn: () =>
      fetchClient({
        apiURL: apiURL.forgeDAPI,
        endpoint: forgeMatchVideosEndpoint,
      }),
    ...queryConfig,
    enabled: !!matchSportecId && status?.abbreviation === statusTypes.post,
    select: (videos) => videos?.items,
  });

  const { data: forgeMatchContentVideos } = useQuery({
    queryKey: [`forge-match-content-videos`, { slug }],
    queryFn: () =>
      fetchClient({
        apiURL: apiURL.forgeDAPI,
        endpoint: forgeMatchContentVideosEndpoint,
      }),
    ...queryConfig,
    enabled: !!slug && status?.abbreviation === statusTypes.post,
    select: (videos) => videos?.items,
  });

  const videoList = React.useMemo(() => {
    const mergedMatchVideos =
      forgeMatchVideos?.length >= 0 && forgeMatchContentVideos?.length >= 0
        ? [...forgeMatchVideos, ...forgeMatchContentVideos]
        : [];

    const matchVideos = removeDuplicates(mergedMatchVideos, '_entityId');

    return composeVideoList(matchVideos, videoTags);
  }, [forgeMatchVideos, forgeMatchContentVideos, videoTags]);

  return (
    <StyledMatchWatch className="mls-c-match-list__match-watch">
      <MatchWatchComponent
        position={position}
        videoList={videoList}
        showChevron={showChevron}
        firstPartyTickets={firstPartyTickets}
        thirdPartyTickets={thirdPartyTickets}
      />
      <MatchHotelBook position={position} />
    </StyledMatchWatch>
  );
};

function MatchWatchComponent({
  position,
  videoList,
  showChevron,
  firstPartyTickets,
  thirdPartyTickets,
}) {
  const {
    slug,
    status,
    competition,
    appleStreamURL,
    matchPageUrl,
  } = useMatchData();
  const { t } = useReactAppState();


  const trackInteraction = (clickItem) => {
    if (!clickItem) {
      return null;
    }

    trackMatchInteraction({
      clickType: "cta label",
      clickItem,
      clickPosition: position,
      match: slug,
      matchState: status?.abbreviation,
      competition: competition?.shortName,
    });
  };

  switch (status?.abbreviation) {
    case statusTypes.post:
      return (
        <MatchWatchPost
          showChevron={showChevron}
          trackFn={() => trackInteraction("replay")}
          videoList={videoList}
          matchPageUrl={matchPageUrl}
        />
      );
    case statusTypes.live:
      return (
        <MatchWatchLive
          showChevron={showChevron}
          matchPageUrl={matchPageUrl}
          appleStreamURL={appleStreamURL}
          trackFn={() =>
            trackInteraction(
              `${t("match_watch_on")} ${t("appletv")}`.toLowerCase()
            )
          }
          forceLightVersion
        />
      );
    case statusTypes.pre:
      return (
        <MatchWatchPre
          matchPageUrl={matchPageUrl}
          showChevron={showChevron}
          trackFn={() => trackInteraction("tickets")}
          firstPartyTickets={firstPartyTickets}
          thirdPartyTickets={thirdPartyTickets}
        />
      );
    default:
      return null;
  }
}
